import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { useFundingIsDue } from '../../hooks/useFundingIsDue';
import { positionsQuery } from '../../hooks/usePositions';
import { ProductType, useProducts } from '../../hooks/useProducts';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { invalidateQueries } from '../../lib/reactQueryHelpers';

/** Returns the most recent product `fundingUpdatedAt`. */
const useMostRecentFunding = (products: ProductType[] | undefined): number | undefined =>
  useMemo(
    () =>
      products?.reduce<number | undefined>(
        (latest, product) =>
          product.fundingUpdatedAt && (!latest || product.fundingUpdatedAt > latest)
            ? product.fundingUpdatedAt
            : latest,
        undefined
      ),
    [products]
  );

/**
 * FundingTracker invalidates the positions query when a product's
 * `fundingUpdatedAt` is updated.
 */
export const FundingTracker = () => {
  const selectedSubaccount = useActiveSelectedSubaccount();
  const queryClient = useQueryClient();
  const { data: products } = useProducts();

  const fundingIsDue = useFundingIsDue(products);
  useProducts(fundingIsDue ? 1000 : undefined);

  const mostRecentFunding = useMostRecentFunding(products);
  const previousMostRecentFunding = useRef<number | undefined>(mostRecentFunding);

  useEffect(() => {
    if (
      mostRecentFunding &&
      (!previousMostRecentFunding.current || mostRecentFunding > previousMostRecentFunding.current)
    ) {
      previousMostRecentFunding.current = mostRecentFunding;
      // Skip invalidating if we don't have a previous `previousMostRecentFunding`.
      if (previousMostRecentFunding && selectedSubaccount?.id) {
        invalidateQueries(
          queryClient,
          positionsQuery({ subaccountId: selectedSubaccount.id }).queryKey
        );
      }
    }
  }, [mostRecentFunding, selectedSubaccount?.id, queryClient]);

  return null;
};
